import { MstSyouninGroupsApi } from "@/api/MstSyouninGroupsApi";
import { Flow, getGroupByUserIdReq, getGroupMemberReq, SyouninGroupDetail, HasPermissionForTargetUserReq, MstSyouninGroup, MstSyouninGroupReq, MstSyouninGroupRes, SyouninUser, SyouninGroupOptions, MstSyouninGroupUser, SyouninGroupCreateReq, } from "@/types/SyouninGroup";

export class MstSyouninGroupsService {

  /**
  * 承認グループの承認者として登録するユーザーを検索します。（すでに他の承認グループに設定されているユーザーは除外されます）
  * @returns
  */
  public async getSelectableSyouninUserList(mstSyouninGroupId?: number): Promise<MstSyouninGroupUser[]> {
    const api = new MstSyouninGroupsApi();
    try {
      return await api.getSelectableSyouninUserList(mstSyouninGroupId);
    } catch (e) {
      return [];
    }
  }

  /**
  * 承認グループ、承認者、所属するユーザーを検索します
  * @returns
  */
  public async searchGroupAndUser(req: MstSyouninGroupReq): Promise<MstSyouninGroupRes[]> {
    const api = new MstSyouninGroupsApi();
    try {
      return await api.searchGroupAndUser(req);
    } catch (e) {
      return [];
    }
  }

  /** 承認グループIDを指定して情報を取得します */
  public async getGroupById(mst_syounin_group_id: number): Promise<SyouninGroupDetail | null> {
    const api = new MstSyouninGroupsApi();
    try {
      return await api.getGroupById(mst_syounin_group_id);
    } catch (e) {
      return null;
    }
  }

  /** 承認グループを新規作成します */
  public async createSyouninGroup(params: SyouninGroupCreateReq): Promise<any> {
    const api = new MstSyouninGroupsApi();
    try {
      return await api.createSyouninGroup(params);
    } catch (e) {
      return e;
    }
  }

  /** 承認グループを更新します */
  public async updateSyouninGroup(params: MstSyouninGroup): Promise<any> {
    const api = new MstSyouninGroupsApi();
    try {
      return await api.updateSyouninGroup(params);
    } catch (e) {
      return null;
    }
  }

  /** 承認グループを削除します */
  public async deleteSyouninGroup(mst_syounin_group_id: number): Promise<any> {
    const api = new MstSyouninGroupsApi();
    try {
      return await api.deleteSyouninGroup(mst_syounin_group_id);
    } catch (e) {
      return null;
    }
  }

  public async getAllOptions(): Promise<SyouninGroupOptions | null> {
    const api = new MstSyouninGroupsApi()
    try {
      return await api.getAllOptions()
    } catch (e) {
      return null
    }
  }

  /**
  * 特定の承認グループ（フロー）を取得します
  * @param targetId
  * @returns
  */
  public async getFlow(targetId: number, syouninType: number, userId: number): Promise<Flow[]> {
    const api = new MstSyouninGroupsApi();
    try {
      return await api.getFlow(targetId, syouninType, userId);
    } catch (e) {
      throw e;
    }
  }

  /**
  * 申請の承認者を取得します
  * @param targetId
  * @returns
  */
  public async getApprover(syouninType: number): Promise<Flow[]> {
    const api = new MstSyouninGroupsApi();
    try {
      return await api.getApprover(syouninType);
    } catch (e) {
      throw e;
    }
  }

  public async getGroupByUserId(userId: number) {
    const api = new MstSyouninGroupsApi()
    const req: getGroupByUserIdReq = {
      userId
    }
    try {
      return await api.getGroupByUserId(req)
    } catch (e) {
      return []
    }
  }

  public async getGroupBySyouninUserId(userId: number) {
    const api = new MstSyouninGroupsApi()
    const req: getGroupByUserIdReq = {
      userId
    }
    try {
      return await api.getGroupBySyouninUserId(req)
    } catch (e) {
      return []
    }
  }

  public async getGroupMember(userId?: number, excludeTaisyokuUser = false) {
    const api = new MstSyouninGroupsApi()
    const req: getGroupMemberReq = {
      userId,
      excludeTaisyokuUser
    }
    try {
      return await api.getGroupMember(req)
    } catch (e) {
      return []
    }
  }

  /** 承認者・確認者を追加します */
  public async addSyouninUsers(params: MstSyouninGroup): Promise<any> {
    const api = new MstSyouninGroupsApi();
    try {
      return await api.addSyouninUsers(params);
    } catch (e) {
      return null;
    }
  }

  /** 承認者・確認者を更新します */
  public async updateSyouninUsers(params: MstSyouninGroupRes): Promise<SyouninUser[] | null> {
    const api = new MstSyouninGroupsApi();
    try {
      return await api.updateSyouninUsers(params);
    } catch (e) {
      return null;
    }
  }

  /** 承認者・確認者を削除します */
  public async deleteSyouninUser(mst_syounin_group_syounin_user_id: number, mst_syounin_group_id: number): Promise<any> {
    const api = new MstSyouninGroupsApi();
    try {
      return await api.deleteSyouninUser(mst_syounin_group_syounin_user_id, mst_syounin_group_id);
    } catch (e) {
      return null;
    }
  }

  /** 自身が承認者のグループに対象ユーザーが所属するか（対象ユーザーに対する権限を持っているか）の情報を取得 */
  public async hasPermissionForTargetUser(targetUserId: number) {
    const api = new MstSyouninGroupsApi();
    const req: HasPermissionForTargetUserReq = {
      targetUserId
    }
    try {
      return await api.hasPermissionForTargetUser(req);
    } catch (e) {
      return [];
    }
  }

  /** 指定グループの承認者か */
  public async isSyouninUser(groupId: number, userId?: number) {
    const api = new MstSyouninGroupsApi()
    try {
      return await api.isSyouninUser(groupId, userId)
    } catch (e) {
      return false
    }
  }

  /** 指定グループの承認者か */
  public async isKakuninUser(groupId: number, userId?: number) {
    const api = new MstSyouninGroupsApi()
    try {
      return await api.isKakuninUser(groupId, userId)
    } catch (e) {
      return false
    }
  }
}
